// Promotions — the pure logic behind `_authed.coupons.tsx`. M7b.
//
// Colocated with the route and prefixed `-` so the TanStack Router plugin
// ignores it (same convention as `-block-fields.tsx`). It lives here rather
// than inside the .tsx so it can be unit-tested under the admin's
// `environment: 'node'` vitest config with no DOM and no JSX.
//
// 🔴 WHY validate() MIRRORS ZOD BY HAND. `couponCreateInput` is a validator
// over a COMPLETE input; the form needs per-field errors on a PARTIAL,
// half-typed draft. Running Zod against partial state and mapping issue paths
// back is more code and worse errors than the ~30 lines below. The drift risk
// is real, so `-promotions.test.ts` runs a fixture table through BOTH and
// asserts they agree on accept/reject for every row. Change one, change both.
//
// 🔴 ALL money goes through lib/money.ts. No inline `/ 100` or `/ 1000` — the
// 3-decimal Gulf currencies (KWD/BHD/OMR) have burned this console three times.

import type { Coupon, CreateCouponInput, PromotionKind } from '~/lib/admin';
import { parseMinorAmount, type Currency } from '~/lib/money';

export const PROMOTION_KINDS: readonly PromotionKind[] = ['coupon', 'offer', 'plan_discount'];

export const KIND_LABEL: Record<PromotionKind, string> = {
  coupon: 'Coupon',
  offer: 'Offer',
  plan_discount: 'Plan discount',
};

/** True for the kinds that apply themselves with no customer input. */
export function isAutomatic(kind: PromotionKind): boolean {
  return kind !== 'coupon';
}

export type DiscountMode = 'percent' | 'amount';

/** Exactly the form's state. Strings, because that is what inputs hold. */
export interface PromotionDraft {
  kind: PromotionKind;
  code: string;
  name: string;
  mode: DiscountMode;
  percent: string;
  amount: string;
  currency: Currency;
  startsAt: string;
  expiresAt: string;
  appliesToPlanId: string;
  appliesToInterval: '' | 'month' | 'year';
  maxRedemptions: string;
  maxRedemptionsPerOwner: string;
}

export type PromotionField =
  | 'code'
  | 'name'
  | 'percent'
  | 'amount'
  | 'startsAt'
  | 'expiresAt'
  | 'appliesToPlanId'
  | 'maxRedemptions'
  | 'maxRedemptionsPerOwner';

export interface ValidationResult {
  ok: boolean;
  errors: Partial<Record<PromotionField, string>>;
}

export function emptyDraft(kind: PromotionKind = 'coupon'): PromotionDraft {
  return {
    kind,
    code: '',
    name: '',
    mode: 'percent',
    percent: '',
    amount: '',
    currency: 'QAR',
    startsAt: '',
    expiresAt: '',
    appliesToPlanId: '',
    appliesToInterval: '',
    maxRedemptions: '',
    maxRedemptionsPerOwner: '',
  };
}

// The API's own code shape (`couponCreateInput`): A-Z, 0-9 and dashes, 2-40
// chars, first character alphanumeric.
const CODE_RE = /^[A-Z0-9][A-Z0-9-]{1,39}$/;

function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * Mirror of every `couponCreateInput` refinement, keyed by form field.
 *
 * The rule from the brief: **the UI must never be able to submit a
 * combination the API rejects.** Two rules here are deliberately STRICTER
 * than the API:
 *
 *  - `name` is required for the automatic kinds. The API allows null, but a
 *    code-less row with a null name renders as a blank cell — an anonymous
 *    thing quietly taking money off checkouts. The schema comment already
 *    calls `name` "the admin-facing label"; this enforces what it intends.
 *  - a fixed amount must PARSE, not merely be non-empty. `parseMinorAmount`
 *    returns null for BOTH empty and refused (1.2345 in KWD has more decimals
 *    than the currency has), and collapsing those two once saved a refused
 *    typo as a real 0 discount.
 */
export function validate(draft: PromotionDraft): ValidationResult {
  const errors: Partial<Record<PromotionField, string>> = {};
  const auto = isAutomatic(draft.kind);

  // ── code: required for `coupon`, forbidden for the automatic kinds ──
  const code = draft.code.trim().toUpperCase();
  if (draft.kind === 'coupon') {
    if (code === '') errors.code = 'A coupon needs a code.';
    else if (!CODE_RE.test(code)) errors.code = 'Use 2-40 characters: A-Z, 0-9 and dashes.';
  } else if (code !== '') {
    errors.code = 'An automatic promotion must not have a code.';
  }

  // ── name: optional for a coupon, required (UI rule) for the rest ──
  if (auto && draft.name.trim() === '') errors.name = 'Give this promotion a name.';
  else if (draft.name.trim().length > 120) errors.name = 'Keep the name under 120 characters.';

  // ── discount: exactly one of percent | fixed amount ──
  if (draft.mode === 'percent') {
    const raw = draft.percent.trim();
    if (raw === '') errors.percent = 'Set a percent.';
    else {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 100)
        errors.percent = 'A whole number between 1 and 100.';
    }
  } else {
    const parsed = parseMinorAmount(draft.amount, draft.currency);
    if (draft.amount.trim() === '') errors.amount = 'Set an amount.';
    else if (parsed == null)
      errors.amount = `Not a valid ${draft.currency} amount — check the decimal places.`;
    else if (parsed <= 0) errors.amount = 'The amount must be greater than zero.';
  }

  // ── window ──
  const startsAt = toIso(draft.startsAt);
  const expiresAt = toIso(draft.expiresAt);
  if (draft.startsAt.trim() !== '' && startsAt == null) errors.startsAt = 'Not a valid date.';
  if (draft.expiresAt.trim() !== '' && expiresAt == null) errors.expiresAt = 'Not a valid date.';
  // An automatic promotion with no end would discount every checkout forever.
  if (auto && draft.expiresAt.trim() === '')
    errors.expiresAt = 'An automatic promotion needs an end date.';
  if (startsAt != null && expiresAt != null && startsAt >= expiresAt)
    errors.startsAt = 'The start must be before the end.';

  // ── plan ──
  if (draft.kind === 'plan_discount' && draft.appliesToPlanId === '')
    errors.appliesToPlanId = 'A plan discount needs a plan.';

  // ── caps ──
  if (draft.maxRedemptions.trim() !== '' && parsePositiveInt(draft.maxRedemptions) == null)
    errors.maxRedemptions = 'A whole number above zero, or leave it blank for unlimited.';
  if (
    draft.maxRedemptionsPerOwner.trim() !== '' &&
    parsePositiveInt(draft.maxRedemptionsPerOwner) == null
  )
    errors.maxRedemptionsPerOwner = 'A whole number above zero, or leave it blank for unlimited.';

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Turn a VALID draft into the POST body.
 *
 * Only ever call this when `validate(draft).ok`. Note the discount branch: the
 * body carries exactly ONE of percent / amount, never both — the schema now
 * refuses both (c9c2169) and both resolvers would otherwise silently apply the
 * percent and leave the amount as dead data.
 */
export function buildCreateInput(draft: PromotionDraft): CreateCouponInput {
  const auto = isAutomatic(draft.kind);
  const percentMode = draft.mode === 'percent';
  return {
    kind: draft.kind,
    code: draft.kind === 'coupon' ? draft.code.trim().toUpperCase() : null,
    name: draft.name.trim() === '' ? null : draft.name.trim(),
    discountPercent: percentMode ? Number(draft.percent.trim()) : null,
    // parseMinorAmount owns the per-currency precision — never re-derive the
    // divisor. validate() has already guaranteed this parses.
    discountAmountMinor: percentMode ? null : parseMinorAmount(draft.amount, draft.currency),
    discountCurrency: percentMode ? null : draft.currency,
    maxRedemptions: parsePositiveInt(draft.maxRedemptions),
    maxRedemptionsPerOwner: parsePositiveInt(draft.maxRedemptionsPerOwner),
    startsAt: toIso(draft.startsAt),
    expiresAt: toIso(draft.expiresAt),
    appliesToPlanId: auto || draft.appliesToPlanId !== '' ? orNull(draft.appliesToPlanId) : null,
    appliesToInterval: draft.appliesToInterval === '' ? null : draft.appliesToInterval,
  };
}

function orNull(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

/**
 * `<input type="datetime-local">` gives `2026-09-01T10:00` — no zone, no
 * seconds. The API wants an ISO datetime. Returns null for empty AND for
 * unparseable, so a bad value is never quietly sent as `Invalid Date`.
 */
export function toIso(local: string): string | null {
  const raw = local.trim();
  if (raw === '') return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// ─── Cross-currency skip (§2.3) ───────────────────────────────────────────

/**
 * True when this promotion silently does nothing for most customers.
 *
 * `resolveAutomaticPromotion` skips a fixed-amount promotion whose currency is
 * not the checkout currency — correct (there is no FX at checkout) but
 * invisible: a QAR offer looks live in the console while doing nothing at all
 * for anyone paying in AED, SAR or KWD. `resolveCoupon` differs — a typed code
 * in the wrong currency throws a visible error — so only the automatic kinds
 * carry this hazard.
 */
export function isCurrencyLimited(row: Coupon): boolean {
  return isAutomatic(row.kind) && row.discountAmountMinor != null && row.discountCurrency != null;
}

// ─── Overlap detection (§2.6) ─────────────────────────────────────────────

interface Window {
  from: number;
  to: number;
}

function windowOf(row: Coupon): Window {
  return {
    from: row.startsAt ? Date.parse(row.startsAt) : Number.NEGATIVE_INFINITY,
    to: row.expiresAt ? Date.parse(row.expiresAt) : Number.POSITIVE_INFINITY,
  };
}

function windowsIntersect(a: Window, b: Window): boolean {
  // Half-open [from, to): the engine uses `expiresAt <= now → skip`, so a
  // promotion ending at exactly the instant another starts does NOT overlap.
  return a.from < b.to && b.from < a.to;
}

/** NULL means "matches everything", exactly as the resolver treats it. */
function nullableMatches(a: string | null, b: string | null): boolean {
  return a == null || b == null || a === b;
}

/**
 * Which automatic promotions compete with which.
 *
 * `resolveAutomaticPromotion` picks the SINGLE BEST discount; every loser is
 * inert. Nothing in the console says so, and two overlapping offers on the
 * same plan look like two live promotions when exactly one is doing anything.
 *
 * Advisory only — overlap is often intentional (a sitewide offer plus a better
 * yearly discount). Comparing MAGNITUDES is deliberately not attempted:
 * percent and fixed cannot be ranked without knowing the checkout amount, and
 * guessing which wins would be worse than saying "only one applies".
 *
 * Returns a set of the ids that overlap at least one other row. Archived rows
 * are excluded — the resolver filters `status = 'active'`.
 */
export function findOverlapping(rows: readonly Coupon[]): Set<string> {
  const candidates = rows.filter((r) => isAutomatic(r.kind) && r.status === 'active');
  const out = new Set<string>();
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const a = candidates[i]!;
      const b = candidates[j]!;
      if (!nullableMatches(a.appliesToPlanId, b.appliesToPlanId)) continue;
      if (!nullableMatches(a.appliesToInterval, b.appliesToInterval)) continue;
      if (!windowsIntersect(windowOf(a), windowOf(b))) continue;
      out.add(a.id);
      out.add(b.id);
    }
  }
  return out;
}

// ─── List helpers ─────────────────────────────────────────────────────────

/** The one identity string for a row: its code, else its name, else its id. */
export function promotionLabel(row: Coupon): string {
  return row.code ?? row.name ?? row.id;
}

export type WindowState = 'scheduled' | 'live' | 'ended' | 'always';

export function windowState(row: Coupon, now: number = Date.now()): WindowState {
  const { from, to } = windowOf(row);
  if (now < from) return 'scheduled';
  // `expiresAt <= now` is the resolver's own boundary — match it exactly.
  if (now >= to) return 'ended';
  return from === Number.NEGATIVE_INFINITY && to === Number.POSITIVE_INFINITY ? 'always' : 'live';
}
